import React, { useCallback, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Field, Form, Formik } from 'formik';
import { Coins } from 'lucide-react';
import commonService from '@/services/common.service';
import FormInput from '@/components/form-components/form-input';
import FormikTextarea from '@/components/form-components/formik-textarea';
import * as Yup from 'yup';
import './AddCreditDialogComponent.scss';

interface AddCreditDialogComponentProps {
    isOpen: boolean;
    onClose: () => void;
    user: any;
}

interface FormValues {
    input_tokens: number;
    output_tokens: number;
    allowed_budget: number;
    comment: string;
    user_id: string;
}

const validationSchema = Yup.object({
    allowed_budget: Yup.number()
        .min(1, 'Allowed budget must be at least $1')
        .required('Allowed budget is required'),
    comment: Yup.string()
        .min(3, 'Comment must be at least 3 characters')
        .required('Comment is required'),
    input_tokens: Yup.number()
        .min(0, 'Input tokens cannot be negative'),
    output_tokens: Yup.number()
        .min(0, 'Output tokens cannot be negative'),
});

const AddCreditDialogComponent: React.FC<AddCreditDialogComponentProps> = ({
    isOpen,
    onClose,
    user
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const initialValues: FormValues = {
        input_tokens: 0,
        output_tokens: 0,
        allowed_budget: 5,
        comment: '',
        user_id: user?.id || ''
    };

    const handleSubmit = useCallback((values: FormValues, { resetForm }: any) => {
        if (!values.user_id) {
            commonService._alert.showToast('User ID is required', 'error');
            return;
        }

        // Create payload with only non-zero token values
        const payload: any = {
            allowed_budget: values.allowed_budget,
            comment: values.comment,
            user_id: values.user_id,
            input_tokens: values.input_tokens || 0,
            output_tokens: values.output_tokens || 0
        };

        setIsSubmitting(true);
        commonService._payments_Service.addTokenCreditsAPICall(payload)
        .then((response: any) => {
            if (response?.data) {
                commonService._alert.showToast('Credits added successfully', 'success');
                onClose();
                resetForm();
            }
        })
        .catch((error: any) => {
            console.error('Error adding credits:', error);
            commonService._alert.showToast(
                error?.response?.data?.message || 'Failed to add credits', 
                'error'
            );
        })
        .finally(() => {
            setIsSubmitting(false);
        });
    }, [onClose]);

    const handleClose = () => {
        if (!isSubmitting) {
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="add-credit-dialog-content">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Coins className="h-5 w-5 text-primary" />
                        Add Token Credits
                    </DialogTitle>
                </DialogHeader>

                <div className="add-credit-form">
                    <div className="user-info mb-4 p-3 bg-muted rounded-lg">
                        <h4 className="font-medium mb-2 text-muted-foreground">User Information</h4>
                        <p className="text-sm text-muted-foreground">
                            <strong>Name:</strong> {user?.name || 'N/A'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                            <strong>Email:</strong> {user?.email || 'N/A'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                            <strong>User ID:</strong> {user?.id || 'N/A'}
                        </p>
                    </div>

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        enableReinitialize={true}
                        validateOnChange={true}
                        validateOnBlur={false}
                    >
                        {({ isValid, dirty }) => (
                            <Form>
                                <div className="form-grid">
                                    <Field name="input_tokens">
                                        {(field: any) => (
                                            <FormInput
                                                label="Input Tokens (Optional)"
                                                placeholder="Enter input tokens (optional)"
                                                type="number"
                                                formikField={field}
                                                className="form-group"
                                            />
                                        )}
                                    </Field>

                                    <Field name="output_tokens">
                                        {(field: any) => (
                                            <FormInput
                                                label="Output Tokens (Optional)"
                                                placeholder="Enter output tokens (optional)"
                                                type="number"
                                                formikField={field}
                                                className="form-group"
                                            />
                                        )}
                                    </Field>

                                    <Field name="allowed_budget">
                                        {(field: any) => (
                                            <FormInput
                                                label="Allowed Budget ($)"
                                                placeholder="Enter allowed budget"
                                                type="number"
                                                formikField={field}
                                                className="form-group"
                                            />
                                        )}
                                    </Field>

                                    <Field name="comment">
                                        {(field: any) => (
                                            <FormikTextarea
                                                label="Comment"
                                                placeholder="Enter a comment for this credit addition"
                                                rows={3}
                                                formikField={field}
                                                className="form-group full-width"
                                            />
                                        )}
                                    </Field>
                                </div>

                                <DialogFooter>
                                    <Button
                                        onClick={handleClose}
                                        disabled={isSubmitting}
                                        className="border border-border bg-background hover:bg-accent"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={isSubmitting || !isValid || !dirty}
                                        className="flex items-center gap-2"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                Adding Credits...
                                            </>
                                        ) : (
                                            <>
                                                <Coins className="h-4 w-4" />
                                                Add Credits
                                            </>
                                        )}
                                    </Button>
                                </DialogFooter>
                            </Form>
                        )}
                    </Formik>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AddCreditDialogComponent; 